<!-- BindCardPopup.vue -->
<template>
    <Popup :showHeader="false" :showPopup="showPopup" @hidePopup="$emit('hidePopup')">
        <div class="bind-card-popup">
            <BackIcon v-active-touch class="back-icon interactive-icon" v-if="isShowBack" @click.native="handleShowRecharge(true)" />
            <CloseIcon v-active-touch class="close-icon interactive-icon" @click.native="$emit('hidePopup')" />
            <span class="bind-popup-title">{{ $t('m.bindPopupTitle') }}</span>
            <div class="loading-stripe" v-if="showStripeLoading">
                <LoadingIcon class="loading-icon" />
            </div>
            <div id="bindCardStripe" ref="bindCardStripe" class="bind-card-stripe"></div>
            <div class="loading-fortis" v-if="showFortisLoading">
                <LoadingIcon class="loading-icon" />
            </div>
            <div :id="fortisContainerId" ref="bindCardFortis" class="bind-card-fortis"></div>
            <span v-active-touch class="primary-btn popup-btn" :class="{ 'invalid': isPaymentBtnInvalid }" @click="submitNewCardPayment">
                <span>{{ $t('m.savePay') }}</span>
            </span>
        </div>
    </Popup>
</template>

<script>
import Popup from "../../../components/popups/Popup.vue";
import BackIcon from "@/components/icons/BackIcon.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import LoadingIcon from "@/components/icons/LoadingIcon.vue";
import {
    createPaymentOrder,
    completePaymentOrder,
    borrow,
    doSubscriptionPayment,
    confirmSubscriptionPayment
} from "@/api/logined";
import { syncClientNextAction } from "@/services/clientNextAction";
import { borrowWithStationBusyRetry } from "@/services/borrowRetry";
import { getFortisEventKey, resolveFortisPaymentMethod } from "@/utils/payment";

let bindCardFortisContainerSeq = 0;
const externalScriptPromises = new Map();

export default {
    components: {
        Popup,
        BackIcon,
        CloseIcon,
        LoadingIcon,
    },
    props: {
        showPopup: {
            type: Boolean,
            required: true
        },
        isShowBack: {
            type: Boolean,
            default: false
        },
        cardGatewayId: {
            type: Number,
            required: true
        },
        stripeGatewayId: {
            type: Number,
            required: true
        },
        fortisGatewayId: {
            type: Number,
            required: true
        },
        customSubmit: {
            type: Boolean,
            default: false
        },
        fortisClientSecret: {
            type: String,
            default: undefined
        },
        stripe: {
            type: Object,
            default: undefined
        },
        selectedSubscription: {
            type: Object,
            default: () => ({})
        },
        authorizationSubscriptionId: {
            type: [String, Number],
            default: undefined
        },
        deviceCode: {
            type: [String, Number],
            required: true
        },
        outletId: {
            type: [String, Number],
            default: undefined
        }
    },
    data() {
        return {
            bindCardValid: false,
            card: undefined,
            clientSecret: undefined,
            rentPaymentId: undefined,
            internalId: undefined,
            subscriptionPaymentId: undefined,
            loadingFortis: true,
            loadingStripe: false,
            isSubmittingPayment: false,
            lastFortisEventKey: undefined,
            fortisContainerId: `bind-card-fortis-${++bindCardFortisContainerSeq}`,
        }
    },
    computed: {
        isStripeGatewaySelected() {
            return this.cardGatewayId === this.stripeGatewayId;
        },
        isFortisGatewaySelected() {
            return this.cardGatewayId === this.fortisGatewayId;
        },
        showStripeLoading() {
            return this.loadingStripe && this.isStripeGatewaySelected;
        },
        showFortisLoading() {
            return this.loadingFortis && this.isFortisGatewaySelected;
        },
        isPaymentBtnInvalid() {
            if (this.isStripeGatewaySelected) {
                return !this.bindCardValid || this.loadingStripe;
            }
            if (this.isFortisGatewaySelected) {
                return this.loadingFortis || this.isSubmittingPayment;
            }
            return true;
        }
    },
    watch: {
        cardGatewayId: {
            handler() {
                this.preloadBindCard().catch(() => {});
            },
            immediate: true,
        },
        showPopup(val) {
            if (val) {
                this.isSubmittingPayment = false;
                this.lastFortisEventKey = undefined;
                this.$nextTick(() => {
                    if (this.cardGatewayId === this.fortisGatewayId && !this.card) {
                        this.mountFortisCard();
                    } else if (this.cardGatewayId === this.stripeGatewayId && !this.card) {
                        this.loadingStripe = true;
                        this.mountStripeCard();
                    }
                });
            } else {
                this.isSubmittingPayment = false;
                this.lastFortisEventKey = undefined;
            }
        }
    },
    methods: {
        getFortisLanguage() {
            return "en-us";
        },
        getFortisRuntimeConfig() {
            const hostname = String(
                (typeof window !== "undefined" && window.location && window.location.hostname) || ""
            ).toLowerCase();
            const isLocalIpHost = hostname === "localhost" || /^(?:\d{1,3}\.){3}\d{1,3}$/.test(hostname);
            const isTestHost = hostname.includes("-test-qr.");
            const useProduction = isLocalIpHost || !isTestHost;

            return {
                scriptUrl: useProduction
                    ? "https://js.fortis.tech/commercejs-v1.0.0.min.js"
                    : "https://js.sandbox.fortis.tech/commercejs-v1.0.0.min.js",
                environment: useProduction ? "production" : "sandbox",
            };
        },
        handleGuestAccessError(error) {
            const code = Number(error && error.code);
            if (code === 50113) {
                this.$emit("guest-required");
                return true;
            }
            if (code === 50114) {
                this.$emit("toast", this.$t(`eMsg.${code}`));
                return true;
            }
            return false;
        },
        async preloadBindCard() {
            if (!this.isFortisGatewaySelected) {
                return;
            }

            const fortisRuntime = this.getFortisRuntimeConfig();
            await this.loadScript(fortisRuntime.scriptUrl);
        },
        emitPaymentError(error) {
            if (this.handleGuestAccessError(error)) {
                return;
            }
            this.$emit("toast", {
                message: this.$t("tMsg.paymentFailed") + ": " + error.message,
                persistent: !!error?.message,
            });
        },
        // 获取用户当前IP
        getLocation() {
            return fetch('https://ipapi.co/json/')
                .then(response => response.json())
                .then(data => {
                    return data.country_code;
                })
                .catch(error => {
                    console.error('Error fetching location:', error);
                    return '';
                });
        },
        async mountStripeCard() {
            const countryCode = await this.getLocation()
			const showPostalCode = ["US", "CA", "GB"].includes(countryCode);

			var card = this.stripe.elements().create("card", {
				hidePostalCode: !showPostalCode,
				style: {
					base: {
						color: "#333",
						fontFamily:
							'"SF Pro Text", Helvetica, sans-serif',
						fontSmoothing: "antialiased",
						fontSize: "14px",
						"::placeholder": {
							color: "#99999999",
						},
					},
					invalid: {
						color: "#FF2A23",
						iconColor: "#FF2A23",
					},
				},
			});

			card.mount(this.$refs.bindCardStripe);

			card.on("ready", () => {
				this.$refs.bindCardStripe.style.display = "flex";
                this.loadingStripe = false;
			});

			this.card = card;

			card.on("change", (event) => {
				this.bindCardValid = event.complete;
			});
        },
        async mountFortisCard() {
            try {
                this.loadingFortis = true;
                const fortisRuntime = this.getFortisRuntimeConfig();
                await this.preloadBindCard();
                let fortisKey;
                if (this.fortisClientSecret) {
                    fortisKey = this.fortisClientSecret;
                    this.clientSecret = fortisKey;
                } else if (this.selectedSubscription?.id) {
                    const autoRenew = Number(this.selectedSubscription?.auto_renew ?? 0);
                    await this.doSubscriptionPayment({
                        subscriptionId: this.selectedSubscription.id,
                        paymentMethod: "web_card",
                        gatewayId: this.fortisGatewayId,
                        outletsId: this.outletId,
                        autoRenew,
                    });
                    fortisKey = this.clientSecret;
                } else {
                    await this.createPaymentOrder(
                        "web_card",
                        this.fortisGatewayId,
                        this.deviceCode
                    );
                    fortisKey = this.clientSecret;
                }

                const card = new Commerce.elements(fortisKey);
                if (this.$refs.bindCardFortis) {
                    this.$refs.bindCardFortis.innerHTML = "";
                    this.$refs.bindCardFortis.style.display = "none";
                }

                var create_data = {
                    container: `#${this.fortisContainerId}`,
                    theme: "default",
                    environment: fortisRuntime.environment,
                    view: "card-single-field",
                    language: this.getFortisLanguage(),
                    defaultCountry: "US",
                    floatingLabels: true,
                    showReceipt: false,
                    showSubmitButton: false,
                    showValidationAnimation: true,
                    hideAgreementCheckbox: true,
                    hideTotal: this.selectedSubscription?.id ? false : true, // 原版 hideTotal: true, 新版有些差异，这里参考原版逻辑
                    digitalWallets: [],
                    appearance: {
                        colorFieldBorder: "#333",
                        colorText: "#333",
                        fontSize: "16px",
                        borderRadius: "5px",
                        trimWhitespace: true
                    },
                    fields: {
                        billing: false,
                    },
                };

                card.create(create_data);
                this.card = card;

                card.on("ready", () => {
                    this.$refs.bindCardFortis.style.display = "flex";
                    this.loadingFortis = false;
                });
                card.on("submitted", () => {
                    this.isSubmittingPayment = true;
                    this.loadingFortis = true;
                    this.$refs.bindCardFortis.style.display = "none";
                    if (this.customSubmit) {
                        this.$emit('show-loading');
                    }
                });
                card.on("done", async (ev) => {
                    const eventKey = getFortisEventKey(ev?.data);
                    if (eventKey && this.lastFortisEventKey === eventKey) {
                        return;
                    }
                    this.lastFortisEventKey = eventKey || this.lastFortisEventKey;

                    if (this.customSubmit) {
                        this.$emit('submit-payment', { gateway: 'fortis', event: ev });
                        return;
                    }
                    try {
                        this.$emit('show-loading');
                        if (this.selectedSubscription?.id) {
                            const autoRenew = Number(
                                this.selectedSubscription?.auto_renew ?? 0
                            );
                            await this.confirmSubscriptionPayment({
                                internalId: this.internalId,
                                paymentId: ev.data.id,
                                requestFrom: 1,
                                autoRenew,
                                reuseStatus: 0,
                            });
                            this.$emit('hidePopup');
                            this.$emit('subscription-success');
                        } else {
                            const subscriptionId = this.authorizationSubscriptionId;
                            const paymentMethod = resolveFortisPaymentMethod(ev.data);
                            const completeOrderRes =
                                await this.completePaymentOrder({
                                    rentPaymentId: this.rentPaymentId,
                                    paymentMethod,
                                    paymentId: ev.data.id,
                                });
                            await this.borrow({
                                code: this.deviceCode,
                                cardId: completeOrderRes.data.method_id,
                                verifyType: completeOrderRes.data.verify_type,
                                verifyOrderId:
                                    completeOrderRes.data.verify_order_id,
                                subscriptionId,
                            });
                            this.$router.push({ name: "borrow_success" });
                        }
                    } catch (error) {
                        this.emitPaymentError(error);
                        this.card = undefined;
                        this.isSubmittingPayment = false;
                    } finally {
                        this.$emit('hide-loading');
                    }
                });
            } catch (error) {
                this.loadingFortis = false;
                this.card = undefined;
                this.isSubmittingPayment = false;
                this.emitPaymentError(error);
            }
        },
        async submitNewCardPayment() {
            if (this.cardGatewayId === this.fortisGatewayId) {
                if (this.isSubmittingPayment) {
                    return;
                }
                this.isSubmittingPayment = true;
                this.card.submit();
                return;
            }
            if (this.customSubmit) {
                this.$emit('submit-payment', { gateway: 'stripe', card: this.card });
                return;
            }
            try {
                this.$emit('show-loading');
                if (this.selectedSubscription?.id) {
                    const autoRenew = Number(
                        this.selectedSubscription?.auto_renew ?? 0
                    );
                    await this.doSubscriptionPayment({
                        subscriptionId: this.selectedSubscription.id,
                        paymentMethod: "web_card",
                        gatewayId: this.stripeGatewayId,
                        outletsId: this.outletId,
                        autoRenew,
                    });
                    await this.stripeConfirmPayment({ card: this.card });
                    await this.confirmSubscriptionPayment({
                        internalId: this.internalId,
                        requestFrom: 1,
                        autoRenew,
                    });
                    this.$emit('hidePopup');
                    this.$emit('subscription-success');
                } else {
                    const subscriptionId = this.authorizationSubscriptionId;
                    await this.createPaymentOrder(
                        "web_card",
                        this.stripeGatewayId,
                        this.deviceCode
                    );
                    const stripeConfirmPaymentRes = await this.stripeConfirmPayment(
                        { card: this.card }
                    );
                    const completeOrderRes = await this.completePaymentOrder({
                        rentPaymentId: this.rentPaymentId,
                        paymentMethod:
                            stripeConfirmPaymentRes.paymentIntent.payment_method,
                        reuseStatus: 0,
                    });
                    await this.borrow({
                        code: this.deviceCode,
                        cardId: completeOrderRes.data.method_id,
                        verifyType: completeOrderRes.data.verify_type,
                        verifyOrderId: completeOrderRes.data.verify_order_id,
                        subscriptionId,
                    });
                    this.$router.push({ name: "borrow_success" });
                }
            } catch (error) {
                this.emitPaymentError(error);
            } finally {
                this.$emit('hide-loading');
            }
        },
        // Stripe 新卡支付
        async stripeConfirmPayment(paymentMethodObj, completeCallback = null) {
            try {
                const res = await this.stripe.confirmCardPayment(this.clientSecret, {
                    payment_method: paymentMethodObj,
                });
                if (res.error) {
                    completeCallback && completeCallback('fail')
                    throw res.error;
                } else {
                    completeCallback && completeCallback('success');
                    return res;
                }
            } catch (error) {
                completeCallback && completeCallback('fail');
                throw error;
            }
        },
        async createPaymentOrder(paymentMethod, gatewayId, code) {
            const res = await createPaymentOrder({
                pay_method_type: paymentMethod,
                gateway_id: gatewayId,
                code: code,
            });
            syncClientNextAction(this, res);
            this.rentPaymentId = res.data?.rent_payment_id;
            return res;
        },
        async completePaymentOrder({
            rentPaymentId,
            paymentMethod = undefined,
            paymentId = undefined,
            metaData = undefined,
            reuseStatus = undefined,
        }) {
            return completePaymentOrder({
                rent_payment_id: rentPaymentId,
                payment_method: paymentMethod,
                payment_id: paymentId,
                meta_data: metaData,
                reuse_status: reuseStatus,
            });
        },
        async borrow({
            code,
            cardId,
            verifyType,
            verifyOrderId,
            subscriptionPaymentId,
            subscriptionId,
        }) {
            const payload = {
                code: code,
                card_id: cardId,
                verify_type: verifyType,
                verify_order_id: verifyOrderId,
                subscription_payment_id: subscriptionPaymentId,
                subscription_id: subscriptionId,
            };
            return borrowWithStationBusyRetry(
                () => borrow(payload),
                {
                    onStationBusyChange: (waiting) => {
                        this.$emit("station-busy-change", waiting);
                    },
                }
            );
        },
        async doSubscriptionPayment({
            subscriptionId,
            paymentMethod,
            gatewayId = 0,
            cardId = 0,
            userCouponId = undefined,
            outletsId = undefined,
            autoRenew = undefined,
        }) {
            const res = await doSubscriptionPayment({
                subscription_id: subscriptionId,
                pay_method_type: paymentMethod,
                gateway_id: gatewayId,
                card_id: cardId,
                user_coupon_id: userCouponId,
                outlets_id: outletsId,
                auto_renew: autoRenew,
                is_from_webapp: 1,
            });
            syncClientNextAction(this, res);
            this.subscriptionPaymentId = res.data?.subscription_payment_id;
            return res;
        },
        async confirmSubscriptionPayment({
            internalId = undefined,
            paymentId = undefined,
            requestFrom,
            autoRenew,
            reuseStatus = undefined,
        }) {
            return confirmSubscriptionPayment({
                internal_id: internalId,
                payment_id: paymentId,
                request_from: requestFrom,
                auto_renew: autoRenew,
                reuse_status: reuseStatus,
            });
        },
        async loadScript(src) {
            if (externalScriptPromises.has(src)) {
                return externalScriptPromises.get(src);
            }

            const existingScript = document.querySelector(`script[src="${src}"]`);
            const isFortisScript = src.includes("fortis.tech/commercejs");
            if (
                (existingScript && existingScript.dataset.loaded === "true") ||
                (isFortisScript && typeof window !== "undefined" && window.Commerce)
            ) {
                return Promise.resolve();
            }

            const promise = new Promise((resolve, reject) => {
                const handleLoad = () => {
                    if (existingScript || script) {
                        (existingScript || script).dataset.loaded = "true";
                    }
                    resolve();
                };
                const handleError = (error) => {
                    externalScriptPromises.delete(src);
                    reject(error);
                };

                let script = existingScript;
                if (!script) {
                    script = document.createElement("script");
                    script.src = src;
                    script.async = true;
                    document.head.appendChild(script);
                }

                script.addEventListener("load", handleLoad, { once: true });
                script.addEventListener("error", handleError, { once: true });
            });

            externalScriptPromises.set(src, promise);
            return promise;
        }
    }
}
</script>
<style lang="scss">
@use '@/styles/variables.scss' as *;

.bind-card-popup {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 16px 110px;
  min-height: 320px;

  .close-icon {
    position: absolute;
    top: 13px;
    right: 12px;
    width: 34px;
    height: 34px;
    padding: 5px;
  }

  .back-icon {
    position: absolute;
    top: 13px;
    left: 8px;
    width: 34px;
    height: 34px;
    padding: 9px;
  }

  .bind-popup-title {
    font-size: 20px;
    font-weight: 590;
    line-height: 23.87px;
    margin-bottom: 12px;
  }

  .bind-card-stripe {
    display: none;
    width: 100%;
    margin-top: 20px;
    height: 45px;
    align-items: center;
    border: 1px solid $border-color;
    border-radius: $border-radius;
    padding: 0 15px;

    &>div {
      width: 100%;
    }
  }

  .bind-card-fortis {
    display: none;
    width: 100%;
    margin-top: 20px;
    margin-bottom: -20px;
  }

  .loading-fortis,
  .loading-stripe {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;

    .loading-icon {
      height: 65px;
    }
  }

  .payment-btn {
    &.invalid {
      background: #E3E3E3;
      color: #A9A9A9;
      pointer-events: none;
    }
  }

  .popup-btn {
      width: $container-width;
      position: fixed;
      bottom: 30px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      align-items: center;
      justify-content: center;
      height: 46px; // 保持原有高度设置，或者根据需要调整
      font-size: 16px;
      font-weight: 500;
      border-radius: $border-radius;

      &.invalid {
        background: #E3E3E3;
        color: #A9A9A9;
        pointer-events: none;
      }
  }
}
</style>
